/**
 * Responsive tab strip — icons + labels, flex-wrap (no horizontal scroll).
 * Container queries shorten labels on narrow widths; tooltips carry full names.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export interface ResponsiveTabStripItem {
  id: string;
  label: string;
  /** Shorter label when the container is narrow (defaults to `label`). */
  shortLabel?: string;
  icon: LucideIcon;
  disabled?: boolean;
  title?: string;
  count?: number;
  testId?: string;
  /** Optional DOM id for aria-labelledby on tabpanels. */
  tabDomId?: string;
  /** Optional aria-controls target id. */
  controlsId?: string;
  /** When true, a subtle group separator renders BEFORE this tab (mirrors the
   *  Home-page card groupings). */
  groupStart?: boolean;
  /** Tiny uppercase group label shown beside the separator on wide widths. */
  groupLabel?: string;
  /**
   * Optional per-tab progress dot (the journey mini-map): 'cleared' renders a
   * small success dot on the icon, 'open' a subtle hollow dot. Absent → no
   * dot. Status must be DERIVED from a pure predicate — never hand-set.
   */
  status?: 'cleared' | 'open';
}

export interface ResponsiveTabStripProps {
  tabs: readonly ResponsiveTabStripItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  testId?: string;
  className?: string;
  navClassName?: string;
  /** Section tabs (underline) vs compact chip tabs (filled segment). */
  variant?: 'section' | 'chip';
  onTabRef?: (id: string, el: HTMLButtonElement | null) => void;
  getAriaCurrent?: (id: string, active: boolean) => 'page' | undefined;
  /**
   * Opt-in single-row "active-label" mode. When true the nav is forced onto a
   * single row (no wrap), only the ACTIVE tab shows its text label, and
   * inactive tabs collapse to icon-only (full name surfaced via tooltip on
   * hover, regardless of container width). Tabs with a `count` keep a small
   * count badge on the icon even when collapsed. Default `false` preserves the
   * existing flex-wrap-with-labels behavior for all other consumers.
   */
  collapseInactiveLabels?: boolean;
}

function tabButtonClass(active: boolean, disabled: boolean, variant: 'section' | 'chip'): string {
  if (variant === 'chip') {
    return cn(
      'inline-flex shrink-0 items-center gap-1 px-2 py-1 type-meta transition-colors border-r border-border last:border-r-0',
      active
        ? 'selected-accent font-semibold'
        : 'text-text-muted hover:text-text-primary hover:bg-state-hover',
      disabled && 'opacity-50 cursor-not-allowed',
    );
  }
  return cn(
    'flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 type-section transition-colors border-b-2 -mb-px',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
    active
      ? 'border-primary text-text-primary font-semibold'
      : 'border-transparent text-text-secondary hover:bg-state-hover hover:text-text-primary',
    disabled && 'opacity-50 cursor-not-allowed',
  );
}

export const ResponsiveTabStrip: React.FC<ResponsiveTabStripProps> = ({
  tabs,
  activeId,
  onSelect,
  ariaLabel,
  testId,
  className,
  navClassName,
  variant = 'section',
  onTabRef,
  getAriaCurrent,
  collapseInactiveLabels = false,
}) => {
  const navClass = collapseInactiveLabels
    ? 'flex w-full flex-nowrap items-center gap-x-0.5 gap-y-0 border-b border-border overflow-x-auto no-scrollbar'
    : variant === 'chip'
      ? 'inline-flex flex-wrap items-stretch border border-border'
      : 'flex w-full flex-wrap items-center gap-x-0.5 gap-y-0 border-b border-border';

  return (
    <div className={cn('@container w-full min-w-0', className)} data-testid={testId}>
      <TooltipProvider delayDuration={300}>
        <nav role="tablist" aria-label={ariaLabel} className={cn(navClass, navClassName)}>
          {tabs.map((tab, idx) => {
            const active = tab.id === activeId;
            const short = tab.shortLabel ?? tab.label;
            const tabAriaLabel =
              tab.count != null ? `${tab.label} (${tab.count})` : tab.label;
            const countSuffix =
              tab.count != null ? (
                <span className="ml-0.5 text-text-secondary">({tab.count})</span>
              ) : null;
            const Icon = tab.icon;
            const buttonTestId = tab.testId ?? `${testId ?? 'tab'}-${tab.id}`;

            return (
              <React.Fragment key={tab.id}>
                {tab.groupStart && idx > 0 && (
                  // FIX 5 — keep the vertical group divider, drop the group-label
                  // text (DEFINE/PLAN/ANALYZE). `groupLabel` stays on the type so
                  // callers don't change, but it no longer renders any words.
                  <span
                    aria-hidden
                    className="flex shrink-0 items-center self-stretch ml-1 pl-2 border-l border-border-subtle"
                  />
                )}
                <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    ref={el => onTabRef?.(tab.id, el)}
                    id={tab.tabDomId}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-current={getAriaCurrent?.(tab.id, active)}
                    aria-controls={tab.controlsId}
                    aria-label={tabAriaLabel}
                    aria-disabled={tab.disabled}
                    disabled={tab.disabled}
                    title={tab.title ?? (tab.disabled ? 'Unavailable' : undefined)}
                    data-testid={buttonTestId}
                    onClick={() => {
                      if (!tab.disabled) onSelect(tab.id);
                    }}
                    className={tabButtonClass(active, Boolean(tab.disabled), variant)}
                  >
                    {collapseInactiveLabels ? (
                      <>
                        {/* Single-row active-label mode: active tab shows
                            icon + full label; inactive tabs collapse to an
                            icon, keeping a small count badge when present. */}
                        <span className="relative inline-flex shrink-0">
                          <Icon className="w-4 h-4 shrink-0" aria-hidden />
                          {!active && tab.count != null && (
                            <span
                              aria-hidden
                              className="absolute -right-1.5 -top-1.5 min-w-[0.9rem] rounded-full bg-primary/15 px-1 text-center text-[8px] font-bold leading-[0.9rem] text-primary"
                            >
                              {tab.count}
                            </span>
                          )}
                          {tab.status && (
                            <span
                              aria-hidden
                              data-status={tab.status}
                              data-testid={`${buttonTestId}-status-dot`}
                              className={cn(
                                'absolute -right-0.5 -bottom-0.5 h-1.5 w-1.5 rounded-full',
                                tab.status === 'cleared'
                                  ? 'bg-success'
                                  : 'border border-text-muted bg-transparent',
                              )}
                            />
                          )}
                        </span>
                        {active && (
                          <span
                            aria-hidden
                            className="max-w-[11rem] truncate whitespace-nowrap"
                          >
                            {tab.label}
                            {countSuffix}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <Icon className="w-4 h-4 shrink-0" aria-hidden />
                        <span
                          aria-hidden
                          className="hidden max-w-[11rem] truncate whitespace-nowrap @[720px]:inline"
                        >
                          {tab.label}
                          {countSuffix}
                        </span>
                        <span
                          aria-hidden
                          className="inline max-w-[7rem] truncate whitespace-nowrap @[720px]:hidden"
                        >
                          {short}
                          {countSuffix}
                        </span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className={collapseInactiveLabels ? undefined : '@[720px]:hidden'}
                >
                  {tabAriaLabel}
                </TooltipContent>
              </Tooltip>
              </React.Fragment>
            );
          })}
        </nav>
      </TooltipProvider>
    </div>
  );
};
